import { extractLinkFromActions } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/ActionHandler/extractLinkFromActions";
import { getCssAlignmentValue } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/Styling/getCssAlignmentValue";
import { LoggingEventParams } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/types";
import { SDUIImageOrIconRenderer } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/ComponentRenderers/ImageOrIcon/SDUIImageOrIconRenderer";
import { SDUITypographyClickWrapper } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/ComponentRenderers/Typography/__internal__/SDUITypographyClickWrapper";
import {
  getCssTextAlign,
  getCssTextJustification,
} from "@faire/deprecated-retailer-visitor-shared/components/SDUI/ComponentRenderers/Typography/styles/textJustificationStyleUtils";
import { getLocaleId } from "@faire/deprecated-retailer-visitor-shared/serialized-data/getLocaleId";
import { getTypographyVariant } from "@faire/sdui/lib/deprecated/getTypographyVariant";
import { getColor } from "@faire/sdui/lib/getColor";
import { ITextElement } from "@faire/web-api--source/indigofair/data/ITextElement";
import { TextDecorator } from "@faire/web-api--source/indigofair/data/TextDecorator";
import { TextStyleType } from "@faire/web-api--source/indigofair/data/TextStyleType";
import { Row } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { cn } from "@faire/web/tools/cn";
import React from "react";

interface IProps {
  textElement: ITextElement;
  loggingData: LoggingEventParams | undefined;
  className?: string | undefined;
  componentStyle?: React.CSSProperties | undefined;
  avoidUnderLineOnHover?: boolean | undefined;
  linkTo?: string | undefined;
}

/**
 * @deprecated use `SDUIText` from `@faire/sdui/elements`
 */
export const SDUITypographyRenderer: React.FC<IProps> = ({
  textElement,
  loggingData,
  className,
  componentStyle,
  avoidUnderLineOnHover,
  linkTo,
}) => {
  // TODO: Handle logging
  const { text, style, prefix_element, suffix_c_t_a, suffix_c_t_a_on_click } =
    textElement;

  // TODO: Remove ternary once ITextElement.TextDecorator references type references are removed
  const typographyVariant = getTypographyVariant(style?.type, [
    style?.decorator === ITextElement.TextDecorator.TEXT_DECORATOR_UNKNOWN
      ? TextDecorator.TEXT_DECORATOR_UNKNOWN
      : (style?.decorator as TextDecorator),
  ]);

  const prefixVerticalAlignment = style?.prefix_element_vertical_alignment;

  const colorToken = getColor(style?.color);
  const { link, nonNavigationActions } = extractLinkFromActions(
    suffix_c_t_a_on_click ?? []
  );

  return (
    <Row
      className={className}
      style={componentStyle}
      align={getCssAlignmentValue(prefixVerticalAlignment, "center")}
      justify={getCssTextJustification(style?.text_justification)}
    >
      {prefix_element ? (
        <>
          <SDUIImageOrIconRenderer
            imageOrIcon={prefix_element}
            parentColorToken={style?.color}
            parentFontStyle={style?.type}
          />
          <Spacer size={style?.type === TextStyleType.LABEL ? "1/2x" : "1x"} />
        </>
      ) : null}
      {linkTo ? (
        <SDUITypographyClickWrapper
          link={linkTo}
          onClickActions={nonNavigationActions}
          loggingData={loggingData}
          color={colorToken}
          decorator={style?.decorator}
        >
          <Typography
            variant={typographyVariant}
            color={colorToken}
            maxLines={style?.max_lines}
            align={getCssTextAlign(style?.text_justification)}
            style={{ hyphens: componentStyle?.hyphens ?? "auto" }}
            lang={getLocaleId() ?? "en"}
            data-test-id="sdui-typography-text"
          >
            {text}
          </Typography>
        </SDUITypographyClickWrapper>
      ) : (
        <Typography
          variant={typographyVariant}
          color={colorToken}
          maxLines={style?.max_lines}
          align={getCssTextAlign(style?.text_justification)}
          style={{ hyphens: componentStyle?.hyphens ?? "auto" }}
          lang={getLocaleId() ?? "en"}
          data-test-id="sdui-typography-text"
        >
          <span
            className={cn(
              "animate-underline-text",
              avoidUnderLineOnHover ? null : "animate-underline-text-hover"
            )}
            style={{
              backgroundImage: `linear-gradient(
              ${colorToken},
              ${colorToken}
            )`,
              fontFamily: "inherit",
              fontSize: "inherit",
              fontWeight: "inherit",
              lineHeight: "inherit",
              letterSpacing: "inherit",
              color: "inherit",
            }}
          >
            {text}
          </span>{" "}
          {suffix_c_t_a ? (
            <SDUITypographyClickWrapper
              onClickActions={nonNavigationActions}
              link={link}
              loggingData={loggingData}
              color={colorToken}
            >
              {suffix_c_t_a}
            </SDUITypographyClickWrapper>
          ) : null}
        </Typography>
      )}
    </Row>
  );
};
